import type { SourceAnchor } from "@shared/schema";

// Format a topic's source anchors into a short human-readable description.
// PDFs and PPTX anchors collapse adjacent page/slide numbers into ranges
// per source file ("pages 3-5, 9 of textbook.pdf"). DOCX headings list
// the section names. Returns "" when there are no anchors. Shared by the
// Blueprint page tooltip and the Teacher Copy export.
export function formatSourceAnchors(anchors: SourceAnchor[] | undefined): string {
  if (!anchors || anchors.length === 0) return "";

  // Group anchors by (kind, source) so we can collapse pages/slides into
  // ranges per file.
  const groups = new Map<string, SourceAnchor[]>();
  for (const a of anchors) {
    const key = `${a.kind}::${a.source || ""}`;
    const arr = groups.get(key) || [];
    arr.push(a);
    groups.set(key, arr);
  }

  const parts: string[] = [];
  Array.from(groups.values()).forEach((group: SourceAnchor[]) => {
    const source = group[0].source || "uploaded material";
    if (group[0].kind === "pdf-page" || group[0].kind === "pptx-slide") {
      // Sort numbers and collapse into ranges (3,4,5 → "3-5").
      const numbers = group
        .map((a: SourceAnchor) => (a.kind === "pdf-page" ? a.page : a.kind === "pptx-slide" ? a.slide : 0))
        .filter((n: number) => n > 0)
        .sort((a: number, b: number) => a - b);
      if (numbers.length === 0) return;
      const ranges: string[] = [];
      let start = numbers[0];
      let prev = numbers[0];
      for (let i = 1; i < numbers.length; i++) {
        if (numbers[i] === prev + 1) {
          prev = numbers[i];
          continue;
        }
        ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
        start = numbers[i];
        prev = numbers[i];
      }
      ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
      const noun =
        group[0].kind === "pdf-page"
          ? numbers.length === 1
            ? "page"
            : "pages"
          : numbers.length === 1
          ? "slide"
          : "slides";
      parts.push(`${noun} ${ranges.join(", ")} of ${source}`);
    } else {
      // docx-heading
      const headings = group
        .map((a: SourceAnchor) => (a.kind === "docx-heading" ? a.heading : ""))
        .filter((h: string) => h.length > 0);
      if (headings.length === 0) return;
      const label = headings.map((h: string) => `"${h}"`).join(", ");
      parts.push(`${headings.length === 1 ? "section" : "sections"} ${label} of ${source}`);
    }
  });
  return "Drawn from " + parts.join("; ");
}
